import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Cloud, ArrowLeft, RefreshCw, Plus, Pencil, Trash2 } from "lucide-react";
import { api } from "@/data/api";
import { useAsync } from "@/hooks/useAsync";
import { PageHeader, StatusBadge, DataTable, Panel, AsyncBoundary, EmptyState, ConfirmDialog } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Tipos suportados pelo backend hoje — Cloudflare aceita mais (NS, SRV,
// CAA...), mas SRV/CAA precisam de campos estruturados que este formulário
// ainda não coleta, então não os oferecemos.
const RECORD_TYPES = ["A", "AAAA", "CNAME", "MX", "TXT"];
const PROXIABLE_TYPES = ["A", "AAAA", "CNAME"];
const emptyForm = { type: "A", name: "", content: "", proxied: false, ttl: "Auto", priority: "10" };

export default function CloudflareZone() {
  const { zoneId } = useParams();
  const { data, loading, error, reload } = useAsync(() => api.getCloudflareZone(zoneId), [zoneId]);
  const [dialog, setDialog] = useState({ open: false, editing: null, form: emptyForm });
  const [confirmDel, setConfirmDel] = useState(null);
  const [saving, setSaving] = useState(false);
  const zone = data?.zone;
  const records = data?.records || [];

  const openNew = () => setDialog({ open: true, editing: null, form: emptyForm });
  const openEdit = (r) => setDialog({ open: true, editing: r.id, form: { type: r.type, name: r.name, content: r.content, proxied: r.proxied, ttl: r.ttl, priority: r.priority != null ? String(r.priority) : "10" } });

  const save = async () => {
    const f = dialog.form;
    const body = { type: f.type, name: f.name, content: f.content, ttl: f.ttl, proxied: PROXIABLE_TYPES.includes(f.type) ? f.proxied : false };
    if (f.type === "MX") body.priority = Number(f.priority) || 0;
    setSaving(true);
    try {
      if (dialog.editing) await api.updateDnsRecord(zoneId, dialog.editing, body);
      else await api.createDnsRecord(zoneId, body);
      toast.success(dialog.editing ? "Registro atualizado." : "Registro criado.");
      setDialog({ open: false, editing: null, form: emptyForm });
      reload();
    } catch (e) {
      toast.error(e.detail || "Não foi possível salvar o registro.");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.deleteDnsRecord(zoneId, confirmDel.id);
      toast.success("Registro excluído.");
      setConfirmDel(null);
      reload();
    } catch (e) {
      toast.error(e.detail || "Não foi possível excluir o registro.");
      setConfirmDel(null);
    }
  };

  return <div className="space-y-6 animate-fade-up">
    <Link to="/cloudflare" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="cfzone-back"><ArrowLeft className="h-4 w-4" /> Voltar para Cloudflare</Link>
    <AsyncBoundary loading={loading} error={error}>
      {!zone ? <EmptyState icon={Cloud} title="Zone não encontrada" description="A zone não existe no inventário local." /> : <>
        <PageHeader icon={Cloud} title={zone.name} description="Registros DNS gerenciados via Cloudflare API." actions={<Button variant="outline" onClick={reload} disabled={loading} className="border-line"><RefreshCw className="h-4 w-4" /> Atualizar</Button>} />
        <div className="grid gap-4 sm:grid-cols-4">
          {[['Status', zone.status || 'Unknown'], ['Plano', zone.plan || 'Unknown'], ['Registros', String(records.length)], ['Último sync', zone.last_sync_at ? new Date(zone.last_sync_at).toLocaleString('pt-BR') : 'Unknown']].map(([label, value]) => <Panel key={label} title={label}><p className="text-sm font-medium text-foreground">{value}</p></Panel>)}
        </div>
        <Panel title="Registros DNS" noPadding action={<Button onClick={openNew} data-testid="cfzone-add-record"><Plus className="h-4 w-4" /> Novo registro</Button>}>
          <DataTable className="border-0" columns={[
            { key: 'type', header: 'Tipo', render: (row) => <span className="rounded border border-line px-1.5 py-0.5 font-mono text-[11px]">{row.type}</span> },
            { key: 'name', header: 'Nome', render: (row) => <span className="font-mono text-xs">{row.name}</span> },
            { key: 'content', header: 'Conteúdo', render: (row) => <span className="font-mono text-xs text-muted-foreground">{row.content}{row.type === 'MX' && row.priority != null ? ` (prioridade ${row.priority})` : ''}</span> },
            { key: 'proxied', header: 'Proxy', render: (row) => row.proxiable === false ? '—' : row.proxied ? 'Proxied' : 'DNS Only' },
            { key: 'ttl', header: 'TTL' },
            { key: 'adoption_state', header: 'Estado' },
            { key: 'missing_since', header: 'Último estado', render: (row) => row.missing_since ? 'Ausente' : row.last_sync_at ? 'Atual' : 'Unknown' },
            { key: 'actions', header: '', align: 'right', render: (row) => (
              <div className="flex items-center justify-end gap-1">
                <button onClick={() => openEdit(row)} data-testid={`cfzone-edit-${row.id}`} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-white/5 hover:text-foreground"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => setConfirmDel(row)} data-testid={`cfzone-del-${row.id}`} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-err/10 hover:text-err"><Trash2 className="h-4 w-4" /></button>
              </div>
            ) },
          ]} rows={records} testid="cfzone-dns-table" />
        </Panel>
      </>}
    </AsyncBoundary>

    <Dialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}>
      <DialogContent className="border-line bg-popover">
        <DialogHeader>
          <DialogTitle className="font-heading">{dialog.editing ? "Editar registro" : "Novo registro DNS"}</DialogTitle>
          <DialogDescription>Gravado direto na zona via API da Cloudflare.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Tipo</Label>
              <Select value={dialog.form.type} onValueChange={(v) => setDialog((d) => ({ ...d, form: { ...d.form, type: v, proxied: PROXIABLE_TYPES.includes(v) ? d.form.proxied : false } }))}>
                <SelectTrigger className="border-line bg-surface" data-testid="cfzone-form-type"><SelectValue /></SelectTrigger>
                <SelectContent className="border-line bg-popover">{RECORD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Nome</Label><Input value={dialog.form.name} onChange={(e) => setDialog((d) => ({ ...d, form: { ...d.form, name: e.target.value } }))} placeholder="@ ou subdomínio" className="border-line bg-surface" data-testid="cfzone-form-name" /></div>
          </div>
          <div className={dialog.form.type === "MX" ? "grid grid-cols-3 gap-3" : ""}>
            <div className={dialog.form.type === "MX" ? "col-span-2 space-y-1.5" : "space-y-1.5"}>
              <Label>Conteúdo / Target</Label>
              <Input value={dialog.form.content} onChange={(e) => setDialog((d) => ({ ...d, form: { ...d.form, content: e.target.value } }))} placeholder="151.247.210.24" className="border-line bg-surface font-mono" data-testid="cfzone-form-content" />
            </div>
            {dialog.form.type === "MX" && (
              <div className="space-y-1.5">
                <Label>Prioridade</Label>
                <Input type="number" min="0" max="65535" value={dialog.form.priority} onChange={(e) => setDialog((d) => ({ ...d, form: { ...d.form, priority: e.target.value } }))} className="border-line bg-surface" data-testid="cfzone-form-priority" />
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className={`flex items-center justify-between rounded-lg border border-line bg-white/[0.02] px-3 py-2 ${PROXIABLE_TYPES.includes(dialog.form.type) ? "" : "opacity-50"}`}>
              <Label className={PROXIABLE_TYPES.includes(dialog.form.type) ? "cursor-pointer" : ""}>Proxy (Proxied)</Label>
              <Switch checked={dialog.form.proxied} disabled={!PROXIABLE_TYPES.includes(dialog.form.type)} onCheckedChange={(v) => setDialog((d) => ({ ...d, form: { ...d.form, proxied: v } }))} data-testid="cfzone-form-proxy" />
            </div>
            <div className="space-y-1.5"><Label>TTL</Label><Input value={dialog.form.ttl} onChange={(e) => setDialog((d) => ({ ...d, form: { ...d.form, ttl: e.target.value } }))} className="border-line bg-surface" placeholder="Auto ou segundos (60–86400)" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setDialog({ open: false, editing: null, form: emptyForm })} className="border-line bg-transparent hover:bg-white/5">Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="cfzone-form-save">{saving ? "Salvando…" : "Salvar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <ConfirmDialog
      open={!!confirmDel}
      onOpenChange={(o) => !o && setConfirmDel(null)}
      title={`Excluir registro ${confirmDel?.type} ${confirmDel?.name}?`}
      description="Esta ação remove o registro direto na zona Cloudflare — não pode ser desfeita."
      confirmLabel="Excluir registro"
      destructive
      onConfirm={doDelete}
    />
  </div>;
}
